/**
 * Shared client card with the Odoo lead search — used by the AC wizard.
 * The parent owns the client fields via `value` + `onChange`. Selecting an
 * Odoo lead fills the fields and stamps odoo_lead_id (D1: leads só no Odoo).
 */

import type { AppShell } from "@/App"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { OdooLeadSearch, clientFromOdooLead } from "@/components/OdooLeadSearch"
import type { SimModule } from "@/lib/api"
import { LeadOriginField } from "@/components/LeadOriginField"

export interface ClientFields {
  lead_id: number | string | null
  odoo_lead_id: number | null
  odoo_lead_name: string | null
  /** Origem da lead (obrigatória): chave + texto quando a origem o pede. */
  lead_origin: string | null
  lead_origin_detail: string
  client_name: string
  client_email: string
  client_phone: string
  address: string
  city: string
  postal_code: string
}

export interface ClientLeadCardProps {
  shell: AppShell | null
  module: SimModule
  value: ClientFields
  onChange: (patch: Partial<ClientFields>) => void
}

export function ClientLeadCard({ shell, module, value, onChange }: ClientLeadCardProps) {
  return (
    <Card>
      <CardHeader><CardTitle>Cliente</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <OdooLeadSearch
          shell={shell}
          module={module}
          linked={value.odoo_lead_id ? { id: value.odoo_lead_id, name: value.odoo_lead_name } : null}
          onSelect={(l) => onChange({ ...clientFromOdooLead(l), odoo_lead_id: l.id, odoo_lead_name: l.name })}
          onClear={() => onChange({ odoo_lead_id: null, odoo_lead_name: null })}
        />

        <LeadOriginField
          value={{ lead_origin: value.lead_origin, lead_origin_detail: value.lead_origin_detail }}
          onChange={(o) => onChange(o)}
        />

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label required>Nome</Label>
            <Input value={value.client_name} onChange={(e) => onChange({ client_name: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>Email</Label>
            <Input type="email" value={value.client_email} onChange={(e) => onChange({ client_email: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>Telefone</Label>
            <Input value={value.client_phone} onChange={(e) => onChange({ client_phone: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>Morada</Label>
            <Input value={value.address} onChange={(e) => onChange({ address: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>Cidade</Label>
            <Input value={value.city} onChange={(e) => onChange({ city: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>Código postal</Label>
            <Input value={value.postal_code} onChange={(e) => onChange({ postal_code: e.target.value })} />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
